import { colord } from "colord";
import type { Properties as CSSProperties, Properties } from "csstype";
import {
  DEFAULT_EN_FONT_NAME,
  DEFAULT_FIXED_FONT_NAME,
  DEFAULT_FONT_NAME,
  DEFAULT_JA_FONT_NAME,
} from "./const";

import { exhaustiveGuardWithDefaultValue } from "../utils/exhaustive-guard";
import {
  defaultEnFontFamily,
  defaultFontFamily,
  defaultJaFontFamily,
  shadowSm,
  shadow_3_4_5,
  shadow_5_5_7,
  shadow_7_10_10,
  shadow_9_13_16,
  textShadowDark,
  textShadowLight,
} from "./style-data";
import type {
  AlignHorizontal,
  Effect,
  IAbsoluteNodePosition,
  IAutomatedProductListNode,
  ICardWrapperNode,
  IDocumentNode,
  IDropShadowEffect,
  IFontName,
  IFormDividerNode,
  IFormImageVideoNode,
  IFormNode,
  IFrameNode,
  IImageNode,
  IInstagramGalleryNode,
  INodeDevice,
  IProductGridListNode,
  IProductXScrollListNode,
  ISectionGroupNode,
  ISingleProductNode,
  ISlotNode,
  ISocialProofNode,
  ISwiperButtonGroupChevron,
  ISwiperButtonGroupNode,
  ISwiperContainerNode,
  ISwiperPaginationBullet,
  ISwiperPaginationFraction,
  ISwiperPaginationNode,
  ITextNode,
  ITimerNode,
  IVideoNode,
  MarginValue,
  OverflowDirection,
  PaddingValue,
  PartialNode,
  PrimaryAxisAlignItems,
  ProductCellWidth,
  StyleReducer,
  Trigger,
  WordBreak,
} from "./type";
import {
  getAspectRatioStyle,
  getBorderColorStyle,
  getBorderRadiusStyle,
  getColorVar,
  getObjectFitStyle,
  getPaintsStyle,
  getRgbString,
  getUnitStyle,
  zeroValue,
} from "./util";

const getPrimaryAxisAlignItemsStyle = (
  primaryAxisAlignItems: PrimaryAxisAlignItems
) => {
  switch (primaryAxisAlignItems) {
    case "MIN":
      return "flex-start";
    case "MAX":
      return "flex-end";
    case "CENTER":
      return "center";
    default:
      return "flex-start";
  }
};

const getCounterAxisAlignItemsStyle = (
  counterAxisAlignItems: PrimaryAxisAlignItems
) => {
  switch (counterAxisAlignItems) {
    case "MIN":
      return "flex-start";
    case "MAX":
      return "flex-end";
    case "CENTER":
      return "center";
    default:
      return "flex-start";
  }
};

const getFontFamily = ({
  fontName,
  language,
  fixed = false,
}: {
  fontName: IFontName;
  language: string;
  fixed?: boolean;
}) => {
  if (fontName.family === "inherit") {
    return "inherit";
  }

  const defaultFontName = getDefaultFontName(language, fixed);

  const fontFamilies: string[] = [];
  if (fontName.family !== defaultFontName) {
    fontFamilies.push(`"${fontName.family}"`);
  }
  fontFamilies.push(defaultFontName, "sans-serif");

  return fontFamilies.join(",");
};

export const getShadow = (shadowString: string): IDropShadowEffect[] => {
  switch (shadowString) {
    case "shadow-sm":
      return shadowSm;
    case "shadow":
      return shadow_3_4_5;
    case "shadow-md":
      return shadow_5_5_7;
    case "shadow-lg":
      return shadow_7_10_10;
    case "shadow-xl":
      return shadow_9_13_16;
    case "":
      return [];
    default:
      throw new Error("Unknown shadow type");
  }
};

export const getConstraintMetric = (
  parentRect: DOMRect,
  childRect: DOMRect
) => {
  const top = childRect.top - parentRect.top;
  const left = childRect.left - parentRect.left;
  const right = parentRect.right - childRect.right;
  const bottom = parentRect.bottom - childRect.bottom;
  return {
    top,
    left,
    right,
    bottom,
    centerX:
      childRect.left +
      childRect.width / 2 -
      (parentRect.left + parentRect.width / 2),
    centerY:
      childRect.top +
      childRect.height / 2 -
      (parentRect.top + parentRect.height / 2),
    scale: {
      left: left / parentRect.width,
      width: childRect.width / parentRect.width,
      right: right / parentRect.width,
      top: top / parentRect.height,
      height: childRect.height / parentRect.height,
      bottom: bottom / parentRect.height,
    },
  };
};

export const getConstraintStyle = (position: IAbsoluteNodePosition) => {
  const styleX = getConstraintXStyle(position);
  const styleY = getConstraintYStyle(position);
  const style: CSSProperties = {
    ...styleX,
    ...styleY,
  };
  if (position.constraintX === "CENTER" && position.constraintY === "CENTER") {
    style["transform"] = `${styleX["transform"]} ${styleY["transform"]}`;
  }
  return style;
};

export const getConstraintXStyle = (position: IAbsoluteNodePosition) => {
  const style: CSSProperties = {};
  switch (position.constraintX) {
    case "LEFT":
      style["left"] = `${position.left}px`;
      break;
    case "RIGHT":
      style["right"] = `${position.right}px`;
      break;
    case "CENTER":
      style["left"] = "50%";
      style["transform"] = `translateX(calc(-50% + ${position.centerX}px))`;
      break;
    case "LEFT_AND_RIGHT":
      style["left"] = `${position.left}px`;
      style["right"] = `${position.right}px`;
      style["width"] = `calc(100% - ${position.left}px - ${position.right}px)`;
      break;
    case "SCALE":
      style["left"] = `calc(${100 * position.scale.left}%)`;
      style["width"] = `calc(${100 * position.scale.width}%)`;
      style["right"] = `calc(${100 * position.scale.right}%)`;
      break;
    default:
      style["left"] = `${position.left}px`;
      break;
  }
  return style;
};

export const getConstraintYStyle = (position: IAbsoluteNodePosition) => {
  const style: CSSProperties = {};
  switch (position.constraintY) {
    case "TOP":
      style["top"] = `${position.top}px`;
      break;
    case "BOTTOM":
      style["bottom"] = `${position.bottom}px`;
      break;
    case "CENTER":
      style["top"] = "50%";
      style["transform"] = `translateY(calc(-50% + ${position.centerY}px))`;
      break;
    case "TOP_AND_BOTTOM":
      style["top"] = `${position.top}px`;
      style["bottom"] = `${position.bottom}px`;
      style["height"] = `calc(100% - ${position.top}px - ${position.bottom}px)`;
      break;
    case "SCALE":
      style["top"] = `calc(100% * ${position.scale.top})`;
      style["height"] = `calc(100% * ${position.scale.height})`;
      style["bottom"] = `calc(100% * ${position.scale.bottom})`;
      break;
    default:
      style["top"] = `${position.top}px`;
      break;
  }
  return style;
};

export const getTextShadow = (shadowString: string): IDropShadowEffect[] => {
  switch (shadowString) {
    case "text-shadow-light":
      return textShadowLight;
    case "text-shadow-dark":
      return textShadowDark;
    case "":
      return [];
    default:
      throw new Error("Unknown text shadow type");
  }
};

export const getWordBreakStyle = (wordBreak: WordBreak) => {
  switch (wordBreak) {
    case "BREAK_ALL":
      return "break-all";
    case "BREAK_WORD":
      return "break-word";
    case "NORMAL":
      return "normal";
    case "KEEP_ALL":
      return "keep-all";
    default:
      return "normal";
  }
};

const cursorPointerTriggers: Trigger["type"][] = [
  "MOUSE_DOWN",
  "MOUSE_UP",
  "ON_CLICK",
  "ON_PRESS",
];

export const applyReducer = (
  style: CSSProperties,
  reducer?: (StyleReducer | false | undefined | null)[] | StyleReducer
) => {
  if (!reducer) {
    return style;
  }

  if (typeof reducer === "function") {
    return reducer(style);
  }

  return reducer.reduce((obj, r) => {
    if (typeof r === "function") {
      return r(obj);
    }
    return obj;
  }, style);
};

export const getFrameStyle = (node: PartialNode<IFrameNode>) => {
  const style: CSSProperties = {};

  if (node.overflow) {
    style.overflow = getOverflowStyle(node.overflow);
    style.scrollbarWidth = "none";
    style.msOverflowStyle = "none";
  } else {
    style.overflow = "hidden";
  }

  /**
   * iOS 버전 17이하에서 border-radius 속성이 있고, overflow-hidden일 때, video 태그에 border-radius가 적용되지 않는 이슈를 해결하기 위한 속성.
   * @see https://gist.github.com/domske/b66047671c780a238b51c51ffde8d3a0
   */
  if (
    node.cornerRadius?.bl?.value ||
    node.cornerRadius?.br?.value ||
    node.cornerRadius?.tl?.value ||
    node.cornerRadius?.tr?.value
  ) {
    style.isolation = "isolate";
  }

  if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.maxWidth) {
    style["maxWidth"] = getUnitStyle(node.maxWidth);
  }

  if (node.minWidth) {
    style["minWidth"] = getUnitStyle(node.minWidth);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";

        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (node.layoutMode?.startsWith("HORIZONTAL")) {
    style["display"] = "flex";
    if (node.layoutMode === "HORIZONTAL") {
      style["flexDirection"] = "row";
    } else {
      style["flexDirection"] = "row-reverse";
    }
    if (node.gap) {
      style["gap"] = `${node.gap}px`;
    }
  } else if (node.layoutMode?.startsWith("VERTICAL")) {
    style["display"] = "flex";
    if (node.layoutMode === "VERTICAL") {
      style["flexDirection"] = "column";
    } else {
      style["flexDirection"] = "column-reverse";
    }
    if (node.gap) {
      style["gap"] = `${node.gap}px`;
    }
  }

  // 내부 요소가 아닌, 프레임 자체의 정렬
  if (
    node.alignHorizontal &&
    (!node.position || node.position.type === "RELATIVE")
  ) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  // 진행방향 내부 요소 정렬
  if (node.primaryAxisAlignItems) {
    style["justifyContent"] = getPrimaryAxisAlignItemsStyle(
      node.primaryAxisAlignItems
    );
  }

  // 진행방향의 수직방향 내부 요소 정렬
  if (node.counterAxisAlignItems) {
    style["alignItems"] = getCounterAxisAlignItemsStyle(
      node.counterAxisAlignItems
    );
  }

  // 진행방향이 가로이고 세로정렬이 없을 때 기본값 flex-start 설정
  if (node.layoutMode === "HORIZONTAL" && !node.counterAxisAlignItems) {
    style["alignItems"] = "flex-start";
  }

  if (shouldApplyPaddingStyle(node.padding)) {
    style["padding"] = getPaddingStyle(node.padding);
  }

  if (node.aspectRatio) {
    style["aspectRatio"] = getAspectRatioStyle(node.aspectRatio);
  }

  if (node.strokes?.[0]?.visible && node.strokeWeight) {
    style["borderWidth"] = `${node.strokeWeight}px`;
    style["borderColor"] = getBorderColorStyle(node.strokes);
    // TODO: 추후 설정을 통해 변경될 수 있도록 수정.
    style["borderStyle"] = "solid";
  }

  if (node.cornerRadius) {
    style["borderRadius"] = getBorderRadiusStyle(node.cornerRadius);
  }

  /**
   * TODO: testing-library/react에서 렌더링 시 background 속성 내 gradient
   * 관련 함수가 모두 무시되어 테스팅이 불가능
   * @see https://github.com/jsdom/cssstyle/issues/148
   */
  if (node.fills) {
    style["background"] = getPaintsStyle(node.fills);
  }

  /** TODO: 이펙트 종류 추가시 로직 변경해야 함.
   *  현재는 effects에 DropShadow 요소 밖에 없어서 로직이 간단함. */
  if (node.effects) {
    style["boxShadow"] = getShadowStyle([...node.effects]);
  }

  if (
    node.reactions?.some(
      (reaction) =>
        reaction.trigger?.type &&
        cursorPointerTriggers.includes(reaction.trigger.type) &&
        // Action이 URL일 때는 action.url이 존재하는 경우에만 cursor pointer 적용
        (reaction.action?.type !== "URL" ||
          (typeof reaction.action?.url === "string" &&
            reaction.action?.url !== ""))
    )
  ) {
    style["cursor"] = "pointer";
  }

  return style;
};

export const getSectionGroupStyle = (node: PartialNode<ISectionGroupNode>) => {
  const style: CSSProperties = {};

  style.overflow = "hidden";

  /**
   * iOS 버전 17이하에서 border-radius 속성이 있고, overflow-hidden일 때, video 태그에 border-radius가 적용되지 않는 이슈를 해결하기 위한 속성.
   * @see https://gist.github.com/domske/b66047671c780a238b51c51ffde8d3a0
   */
  if (
    node.cornerRadius?.bl?.value ||
    node.cornerRadius?.br?.value ||
    node.cornerRadius?.tl?.value ||
    node.cornerRadius?.tr?.value
  ) {
    style.isolation = "isolate";
  }

  if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";

        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (node.layoutMode === "HORIZONTAL") {
    style["display"] = "flex";
    style["flexDirection"] = "row";
  } else if (node.layoutMode === "VERTICAL") {
    style["display"] = "flex";
    style["flexDirection"] = "column";
  }

  // 내부 요소가 아닌, 프레임 자체의 정렬
  if (
    node.alignHorizontal &&
    (!node.position || node.position.type === "RELATIVE")
  ) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  // 진행방향 내부 요소 정렬
  if (node.primaryAxisAlignItems) {
    style["justifyContent"] = getPrimaryAxisAlignItemsStyle(
      node.primaryAxisAlignItems
    );
  }

  // 진행방향의 수직방향 내부 요소 정렬
  if (node.counterAxisAlignItems) {
    style["alignItems"] = getCounterAxisAlignItemsStyle(
      node.counterAxisAlignItems
    );
  }

  // 진행방향이 가로이고 세로정렬이 없을 때 기본값 flex-start 설정
  if (node.layoutMode === "HORIZONTAL" && !node.counterAxisAlignItems) {
    style["alignItems"] = "flex-start";
  }

  if (shouldApplyPaddingStyle(node.padding)) {
    style["padding"] = getPaddingStyle(node.padding);
  }

  if (node.aspectRatio) {
    style["aspectRatio"] = getAspectRatioStyle(node.aspectRatio);
  }

  if (node.strokes?.[0]?.visible && node.strokeWeight) {
    style["borderWidth"] = `${node.strokeWeight}px`;
    style["borderColor"] = getBorderColorStyle(node.strokes);
    // TODO: 추후 설정을 통해 변경될 수 있도록 수정.
    style["borderStyle"] = "solid";
  }

  if (node.cornerRadius) {
    style["borderRadius"] = getBorderRadiusStyle(node.cornerRadius);
  }

  /**
   * TODO: testing-library/react에서 렌더링 시 background 속성 내 gradient
   * 관련 함수가 모두 무시되어 테스팅이 불가능
   * @see https://github.com/jsdom/cssstyle/issues/148
   */
  if (node.fills) {
    style["background"] = getPaintsStyle(node.fills);
  }

  /** TODO: 이펙트 종류 추가시 로직 변경해야 함.
   *  현재는 effects에 DropShadow 요소 밖에 없어서 로직이 간단함. */
  if (node.effects) {
    style["boxShadow"] = getShadowStyle([...node.effects]);
  }

  if (
    node.reactions?.some(
      (reaction) =>
        reaction.trigger?.type &&
        cursorPointerTriggers.includes(reaction.trigger.type) &&
        // Action이 URL일 때는 action.url이 존재하는 경우에만 cursor pointer 적용
        (reaction.action?.type !== "URL" ||
          (typeof reaction.action?.url === "string" &&
            reaction.action?.url !== ""))
    )
  ) {
    style["cursor"] = "pointer";
  }

  return style;
};

export const getSectionGroupNavStyle = ({
  fixed,
  node,
  boundingBoxLeft,
  device,
}: {
  fixed: boolean;
  node: PartialNode<ISectionGroupNode>;
  boundingBoxLeft: number;
  device?: INodeDevice | null;
}): Properties => {
  const result: Properties = {};
  const defaultPadding = 20;
  const navMarginTop =
    (device === "DESKTOP" && node.desktopNavMarginTop
      ? node.desktopNavMarginTop
      : node.navMarginTop) ?? 0;

  if (fixed) {
    result.position = "fixed";
    result.top = `${navMarginTop}px`;
    result.padding = `${getUnitStyle(node.navPadding?.top)} calc(${getUnitStyle(
      node.navPadding?.right ?? { value: defaultPadding, unit: "px" }
    )} + ${boundingBoxLeft}px) ${getUnitStyle(
      node.navPadding?.bottom
    )} calc(${getUnitStyle(
      node.navPadding?.left ?? { value: defaultPadding, unit: "px" }
    )} + ${boundingBoxLeft}px)`;
  } else {
    result.position = "absolute";
    result.padding = getPaddingStyle(
      node.navPadding ?? {
        bottom: zeroValue,
        left: {
          value: defaultPadding,
          unit: "px",
        },
        right: {
          value: defaultPadding,
          unit: "px",
        },
        top: zeroValue,
      }
    );
  }

  return result;
};

export const getSectionGroupNavItemStyle = ({
  node,
  index,
  length,
  language = "ko",
}: {
  node: PartialNode<ISectionGroupNode>;
  index: number;
  length: number;
  language?: string;
}): CSSProperties => {
  const result: CSSProperties = {};
  if (node.navAlignHorizontal === "JUSTIFIED") {
    result.flex = "1 0 0";
  } else {
    const type =
      index === 0 ? "first" : index === length - 1 ? "last" : "middle";

    switch (type) {
      case "first": {
        if (
          node.navAlignHorizontal === "RIGHT" ||
          node.navAlignHorizontal === "CENTER"
        ) {
          result.marginLeft = "auto";
        }
        break;
      }

      case "last": {
        if (
          node.navAlignHorizontal === "LEFT" ||
          node.navAlignHorizontal === "CENTER"
        ) {
          result.marginRight = "auto";
        }
        break;
      }
      case "middle":
        break;
    }
  }

  Object.assign(result, {
    "--cb-section-group-nav-item-padding": node.navItemPadding
      ? getPaddingStyle(node.navItemPadding)
      : "10px 20px",
  });

  Object.assign(result, {
    "--cb-section-group-nav-item-font-size": getUnitStyle(
      node.navFontSize ?? { value: 14, unit: "px" }
    ),
  });

  if (node.navFontName) {
    Object.assign(result, {
      "--cb-section-group-nav-item-font-family": getFontFamily({
        fontName: node.navFontName,
        language,
      }),
      "--cb-section-group-nav-item-font-weight": node.navFontName.style,
    });
  }

  return result;
};

export const getDocumentStyle = (
  node: PartialNode<IDocumentNode>,
  language: string = "ko",
  // TODO: 타입 정의
  {
    forceHorizontalAlign,
    defaultFont,
  }: {
    forceHorizontalAlign?: "center" | "left" | "right";
    defaultFont?: string;
  } = {}
): CSSProperties => {
  const style: CSSProperties = {};

  if (defaultFont) {
    style["fontFamily"] = defaultFont;
  } else {
    if (language === "en") {
      style["fontFamily"] = defaultEnFontFamily;
    } else if (language === "ja") {
      style["fontFamily"] = defaultJaFontFamily;
    } else {
      style["fontFamily"] = defaultFontFamily;
    }
  }

  style["lineHeight"] = "1.5";
  style["position"] = "relative";

  if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.maxWidth) {
    style["maxWidth"] = getUnitStyle(node.maxWidth);
  }

  if (node.backgrounds) {
    style["background"] = getPaintsStyle(node.backgrounds);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  }

  if (forceHorizontalAlign) {
    style["margin"] = getMarginByAlignHorizontal(
      forceHorizontalAlign === "center"
        ? "CENTER"
        : forceHorizontalAlign === "left"
        ? "LEFT"
        : forceHorizontalAlign === "right"
        ? "RIGHT"
        : "CENTER"
    );
  } else if (node.alignHorizontal) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  return applyReducer(style);
};

export const getTextAlignHorizontalStyle = (
  textAlignHorizontal?: AlignHorizontal
) => {
  switch (textAlignHorizontal) {
    case "LEFT":
      return "left";
    case "CENTER":
      return "center";
    case "RIGHT":
      return "right";
    default:
      return "left";
  }
};

export const getMarginByAlignHorizontal = (align: AlignHorizontal) => {
  return align === "CENTER"
    ? "0 auto"
    : align === "RIGHT"
    ? "0 0 0 auto"
    : align === "LEFT"
    ? "0 auto 0 0"
    : undefined;
};

export const shouldApplyPaddingStyle = (
  padding?: PaddingValue | null
): padding is PaddingValue => {
  if (
    !padding ||
    (padding.bottom?.value === 0 &&
      padding.top?.value === 0 &&
      padding.left?.value === 0 &&
      padding.right?.value === 0)
  ) {
    return false;
  }
  return true;
};

export const getPaddingStyle = (padding: PaddingValue) => {
  return `${getUnitStyle(padding.top || zeroValue)} ${getUnitStyle(
    padding.right || zeroValue
  )} ${getUnitStyle(padding.bottom || zeroValue)} ${getUnitStyle(
    padding.left || zeroValue
  )}`;
};

export const getMarginStyle = (margin: MarginValue) => {
  return `${getUnitStyle(margin.top || zeroValue)} ${getUnitStyle(
    margin.right || zeroValue
  )} ${getUnitStyle(margin.bottom || zeroValue)} ${getUnitStyle(
    margin.left || zeroValue
  )}`;
};

export const getVideoStyle = (
  node: PartialNode<IVideoNode | IImageNode | IFormImageVideoNode>
) => {
  const style: CSSProperties = {};

  style["overflow"] = "hidden";

  /**
   * iOS 버전 17이하에서 border-radius 속성이 있고, overflow-hidden일 때, video 태그에 border-radius가 적용되지 않는 이슈를 해결하기 위한 속성.
   * @see https://gist.github.com/domske/b66047671c780a238b51c51ffde8d3a0
   */
  if (
    node.cornerRadius?.bl?.value ||
    node.cornerRadius?.br?.value ||
    node.cornerRadius?.tl?.value ||
    node.cornerRadius?.tr?.value
  ) {
    style.isolation = "isolate";
  }

  if (
    node.alignHorizontal &&
    (!node.position || node.position.type === "RELATIVE")
  ) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  if (shouldApplyPaddingStyle(node.padding)) {
    style["padding"] = getPaddingStyle(node.padding);
  }

  if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";
        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (
    node.reactions?.some(
      (reaction) =>
        reaction.trigger?.type &&
        cursorPointerTriggers.includes(reaction.trigger.type) &&
        // Action이 URL일 때는 action.url이 존재하는 경우에만 cursor pointer 적용
        (reaction.action?.type !== "URL" ||
          (typeof reaction.action?.url === "string" &&
            reaction.action?.url !== ""))
    )
  ) {
    style["cursor"] = "pointer";
  }

  if (node.strokes?.[0]?.visible && node.strokeWeight) {
    style["borderWidth"] = `${node.strokeWeight}px`;
    style["borderColor"] = getBorderColorStyle(node.strokes);
    // TODO: 추후 설정을 통해 변경될 수 있도록 수정.
    style["borderStyle"] = "solid";
  }

  // if (node.cornerRadius) {
  //   style["borderRadius"] = getBorderRadiusStyle(node.cornerRadius);
  // }

  /** TODO: 이펙트 종류 추가시 로직 변경해야 함.
   *  현재는 effects에 DropShadow 요소 밖에 없어서 로직이 간단함. */
  if (node.effects) {
    style["boxShadow"] = getShadowStyle([...node.effects]);
  }

  return style;
};

export const getTextStyle = (
  node: PartialNode<ITextNode>,
  language: string = "ko"
) => {
  const style: CSSProperties = {};

  style["wordBreak"] = "break-all";
  if (node.wordBreak) {
    style["wordBreak"] = getWordBreakStyle(node.wordBreak);
  }

  if (node.textAlignHorizontal) {
    style["textAlign"] = getTextAlignHorizontalStyle(node.textAlignHorizontal);
  }

  if (
    node.alignHorizontal &&
    (!node.position || node.position.type === "RELATIVE")
  ) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  if (shouldApplyPaddingStyle(node.padding)) {
    style["padding"] = getPaddingStyle(node.padding);
  }

  if (node.textAutoResize === "WIDTH_AND_HEIGHT") {
    style["width"] = "auto";
    if (node.position?.type === "ABSOLUTE") {
      style["whiteSpace"] = "nowrap";
    }
  } else if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";
        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (node.fontName) {
    style["fontFamily"] = getFontFamily({
      fontName: node.fontName,
      language,
    });
    style["fontWeight"] = node.fontName.style;
  }
  if (node.fontSize) {
    style["fontSize"] = getUnitStyle(node.fontSize);
  }
  if (node.lineHeight) {
    style["lineHeight"] = getUnitStyle(node.lineHeight);
  }
  if (node.letterSpacing) {
    style["letterSpacing"] = getUnitStyle(node.letterSpacing);
  }

  if (node.fills) {
    // TODO: 현재 컬러의 종류가 단색인 케이스만 처리. 추후 다양한 컬러 지원 필요
    const paint = node.fills?.[0];
    if (paint && paint.type === "SOLID") {
      style["color"] = getRgbString(paint.color, paint.opacity);
    }
  }

  if (node.effects) {
    style["textShadow"] = getTextShadowStyle([...node.effects]);
  }

  if (node.backgrounds) {
    style["background"] = getPaintsStyle(node.backgrounds);
  }

  if (node.strokes?.[0]?.visible && node.strokeWeight) {
    style["borderWidth"] = `${node.strokeWeight}px`;
    style["borderColor"] = getBorderColorStyle(node.strokes);
    // TODO: 추후 설정을 통해 변경될 수 있도록 수정.
    style["borderStyle"] = "solid";
  }

  if (node.cornerRadius) {
    style["borderRadius"] = getBorderRadiusStyle(node.cornerRadius);
  }

  /** TODO: 이펙트 종류 추가시 로직 변경해야 함.
   *  현재는 effects에 DropShadow 요소 밖에 없어서 로직이 간단함. */
  if (node.boxShadowEffects && node.boxShadowEffects.length > 0) {
    style["boxShadow"] = getShadowStyle([...node.boxShadowEffects]);
  }

  if (
    node.reactions?.some(
      (reaction) =>
        reaction.trigger?.type &&
        cursorPointerTriggers.includes(reaction.trigger.type) &&
        // Action이 URL일 때는 action.url이 존재하는 경우에만 cursor pointer 적용
        (reaction.action?.type !== "URL" ||
          (typeof reaction.action?.url === "string" &&
            reaction.action?.url !== ""))
    )
  ) {
    style["cursor"] = "pointer";
  }

  return style;
};

export const getTimerStyle = (
  node: PartialNode<ITimerNode>,
  language: string = "ko"
) => {
  const style: CSSProperties = {};

  style["wordBreak"] = "break-all";
  style["fontFeatureSettings"] = "'tnum'";
  style["fontVariantNumeric"] = "tabular-nums";

  if (node.textAlignHorizontal) {
    style["textAlign"] = getTextAlignHorizontalStyle(node.textAlignHorizontal);
  }

  if (
    node.alignHorizontal &&
    (!node.position || node.position.type === "RELATIVE")
  ) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  if (shouldApplyPaddingStyle(node.padding)) {
    style["padding"] = getPaddingStyle(node.padding);
  }

  if (node.textAutoResize === "WIDTH_AND_HEIGHT") {
    style["width"] = "auto";
    if (node.position?.type === "ABSOLUTE") {
      style["whiteSpace"] = "nowrap";
    }
  } else if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";
        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (node.fontName) {
    style["fontFamily"] = getFontFamily({
      fontName: node.fontName,
      language,
    });
    style["fontWeight"] = node.fontName.style;
  }
  if (node.fontSize) {
    style["fontSize"] = getUnitStyle(node.fontSize);
  }
  if (node.lineHeight) {
    style["lineHeight"] = getUnitStyle(node.lineHeight);
  }
  if (node.letterSpacing) {
    style["letterSpacing"] = getUnitStyle(node.letterSpacing);
  }

  if (node.fills) {
    // TODO: 현재 컬러의 종류가 단색인 케이스만 처리. 추후 다양한 컬러 지원 필요
    const paint = node.fills?.[0];
    if (paint && paint.type === "SOLID") {
      style["color"] = getRgbString(paint.color, paint.opacity);
    }
  }

  if (node.effects) {
    style["textShadow"] = getTextShadowStyle([...node.effects]);
  }

  if (node.backgrounds) {
    style["background"] = getPaintsStyle(node.backgrounds);
  }

  if (node.strokes?.[0]?.visible && node.strokeWeight) {
    style["borderWidth"] = `${node.strokeWeight}px`;
    style["borderColor"] = getBorderColorStyle(node.strokes);
    // TODO: 추후 설정을 통해 변경될 수 있도록 수정.
    style["borderStyle"] = "solid";
  }

  if (node.cornerRadius) {
    style["borderRadius"] = getBorderRadiusStyle(node.cornerRadius);
  }

  /** TODO: 이펙트 종류 추가시 로직 변경해야 함.
   *  현재는 effects에 DropShadow 요소 밖에 없어서 로직이 간단함. */
  if (node.boxShadowEffects && node.boxShadowEffects.length > 0) {
    style["boxShadow"] = getShadowStyle([...node.boxShadowEffects]);
  }

  if (
    node.reactions?.some(
      (reaction) =>
        reaction.trigger?.type &&
        cursorPointerTriggers.includes(reaction.trigger.type) &&
        // Action이 URL일 때는 action.url이 존재하는 경우에만 cursor pointer 적용
        (reaction.action?.type !== "URL" ||
          (typeof reaction.action?.url === "string" &&
            reaction.action?.url !== ""))
    )
  ) {
    style["cursor"] = "pointer";
  }

  return style;
};

export const getCardWrapperStyle = (
  node: PartialNode<ICardWrapperNode>,
  _language: string = "ko"
) => {
  const style: CSSProperties = {};

  if (node.maxWidth) {
    style["maxWidth"] = getUnitStyle(node.maxWidth);
  }

  if (node.minWidth) {
    style["minWidth"] = getUnitStyle(node.minWidth);
  }

  if (node.padding) {
    style["padding"] = getPaddingStyle(node.padding);
  }

  if (node.alignHorizontal) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  return style;
};

export const getCardWrapperLayoutStyle = (
  layout: PartialNode<ICardWrapperNode>["layout"],
  _language: string = "ko"
): CSSProperties => {
  const style: CSSProperties = {};

  if (layout?.type === "GRID") {
    style["display"] = "grid";
    style["gridTemplateColumns"] = `repeat(${layout.col}, minmax(0, 1fr))`;
    if (layout.rowGap && layout.colGap) {
      style["gap"] = `${layout.rowGap}px ${layout.colGap}px`;
    } else if (layout.rowGap) {
      style["gap"] = `${layout.rowGap}px 0`;
    } else if (layout.colGap) {
      style["gap"] = `0 ${layout.colGap}px`;
    }
    if (layout.row >= 1) {
      style["gridTemplateRows"] = `repeat(${layout.row}, 1fr)`;
    }
  } else {
    style["display"] = "flex";
    style["flexDirection"] = "column";
    if (layout?.gap) {
      style["gap"] = `${layout.gap}px`;
    }
  }

  return style;
};

export const getSocialProofStyle = (
  node: PartialNode<ISocialProofNode>,
  language: string = "ko"
) => {
  const style: CSSProperties = {};

  style["wordBreak"] = "break-all";
  style["fontFeatureSettings"] = "'tnum'";
  style["fontVariantNumeric"] = "tabular-nums";

  if (node.textAlignHorizontal) {
    style["textAlign"] = getTextAlignHorizontalStyle(node.textAlignHorizontal);
  }

  if (
    node.alignHorizontal &&
    (!node.position || node.position.type === "RELATIVE")
  ) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  if (shouldApplyPaddingStyle(node.padding)) {
    style["padding"] = getPaddingStyle(node.padding);
  }

  if (node.textAutoResize === "WIDTH_AND_HEIGHT") {
    style["width"] = "auto";
    if (node.position?.type === "ABSOLUTE") {
      style["whiteSpace"] = "nowrap";
    }
  } else if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";
        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (node.fontName) {
    style["fontFamily"] = getFontFamily({
      fontName: node.fontName,
      language,
    });
    style["fontWeight"] = node.fontName.style;
  }
  if (node.fontSize) {
    style["fontSize"] = getUnitStyle(node.fontSize);
  }
  if (node.lineHeight) {
    style["lineHeight"] = getUnitStyle(node.lineHeight);
  }
  if (node.letterSpacing) {
    style["letterSpacing"] = getUnitStyle(node.letterSpacing);
  }

  if (node.fills) {
    // TODO: 현재 컬러의 종류가 단색인 케이스만 처리. 추후 다양한 컬러 지원 필요
    const paint = node.fills?.[0];
    if (paint && paint.type === "SOLID") {
      style["color"] = getRgbString(paint.color, paint.opacity);
    }
  }

  if (node.effects) {
    style["textShadow"] = getTextShadowStyle([...node.effects]);
  }

  if (node.backgrounds) {
    style["background"] = getPaintsStyle(node.backgrounds);
  }

  if (node.strokes?.[0]?.visible && node.strokeWeight) {
    style["borderWidth"] = `${node.strokeWeight}px`;
    style["borderColor"] = getBorderColorStyle(node.strokes);
    // TODO: 추후 설정을 통해 변경될 수 있도록 수정.
    style["borderStyle"] = "solid";
  }

  if (node.cornerRadius) {
    style["borderRadius"] = getBorderRadiusStyle(node.cornerRadius);
  }

  /** TODO: 이펙트 종류 추가시 로직 변경해야 함.
   *  현재는 effects에 DropShadow 요소 밖에 없어서 로직이 간단함. */
  if (node.boxShadowEffects && node.boxShadowEffects.length > 0) {
    style["boxShadow"] = getShadowStyle([...node.boxShadowEffects]);
  }

  if (
    node.reactions?.some(
      (reaction) =>
        reaction.trigger?.type &&
        cursorPointerTriggers.includes(reaction.trigger.type) &&
        // Action이 URL일 때는 action.url이 존재하는 경우에만 cursor pointer 적용
        (reaction.action?.type !== "URL" ||
          (typeof reaction.action?.url === "string" &&
            reaction.action?.url !== ""))
    )
  ) {
    style["cursor"] = "pointer";
  }

  return style;
};

// 이미지 로딩중 영역을 미리 잡아놓기 위해 wrapper를 사용
export const getImageStyle = (node: PartialNode<IImageNode>) => {
  const style: CSSProperties = {};

  style["overflow"] = "hidden";
  if (
    node.alignHorizontal &&
    (!node.position || node.position.type === "RELATIVE")
  ) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  if (shouldApplyPaddingStyle(node.padding)) {
    style["padding"] = getPaddingStyle(node.padding);
  }

  if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";
        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (
    node.reactions?.some(
      (reaction) =>
        reaction.trigger?.type &&
        cursorPointerTriggers.includes(reaction.trigger.type) &&
        // Action이 URL일 때는 action.url이 존재하는 경우에만 cursor pointer 적용
        (reaction.action?.type !== "URL" ||
          (typeof reaction.action?.url === "string" &&
            reaction.action?.url !== ""))
    )
  ) {
    style["cursor"] = "pointer";
  }

  if (node.strokes?.[0]?.visible && node.strokeWeight) {
    style["borderWidth"] = `${node.strokeWeight}px`;
    style["borderColor"] = getBorderColorStyle(node.strokes);
    // TODO: 추후 설정을 통해 변경될 수 있도록 수정.
    style["borderStyle"] = "solid";
  }

  if (node.cornerRadius) {
    style["borderRadius"] = getBorderRadiusStyle(node.cornerRadius);
  }

  /** TODO: 이펙트 종류 추가시 로직 변경해야 함.
   *  현재는 effects에 DropShadow 요소 밖에 없어서 로직이 간단함. */
  if (node.effects) {
    style["boxShadow"] = getShadowStyle([...node.effects]);
  }

  return style;
};

export const getInstagramGalleryStyle = (
  node: PartialNode<IInstagramGalleryNode>
) => {
  const style: CSSProperties = {};

  style["display"] = "grid";
  style["overflow"] = "hidden";

  if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";
        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (
    node.alignHorizontal &&
    (!node.position || node.position.type === "RELATIVE")
  ) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  if (node.padding) {
    style["padding"] = getPaddingStyle(node.padding);
  }

  if (node.fills) {
    style["background"] = getPaintsStyle(node.fills);
  }

  if (node.effects) {
    style["boxShadow"] = getShadowStyle([...node.effects]);
  }

  if (node.cornerRadius) {
    style["borderRadius"] = getBorderRadiusStyle(node.cornerRadius);
  }

  if (node.gap) {
    style["gap"] = `${node.gap}px`;
  }

  if (node.col) {
    style["gridTemplateColumns"] = `repeat(${node.col}, 1fr)`;
  }

  if (node.row) {
    style["gridTemplateRows"] = `repeat(${node.row}, 1fr)`;
  }

  return style;
};

// TODO: SlotNode Style 보강 필요!
export const getSlotStyle = (node: PartialNode<ISlotNode>) => {
  const style: CSSProperties = {};
  style.overflow = "hidden";

  if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";

        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }
  return style;
};

export const getSingleProductStyle = (
  node: PartialNode<ISingleProductNode>
) => {
  const style: CSSProperties = {};

  style.overflow = "hidden";

  if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";

        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (node.cornerRadius) {
    style["borderRadius"] = getBorderRadiusStyle(node.cornerRadius);
  }

  if (node.effects) {
    style["boxShadow"] = getShadowStyle([...node.effects]);
  }

  if (node.fills) {
    style["background"] = getPaintsStyle(node.fills);
  }

  // 내부 요소가 아닌, 노드 자체 정렬
  if (
    node.alignHorizontal &&
    (!node.position || node.position.type === "RELATIVE")
  ) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  if (node.aspectRatio) {
    style["aspectRatio"] = getAspectRatioStyle(node.aspectRatio);
  }

  return style;
};

export const getProductItemWidth = (
  cellWidth: ProductCellWidth,
  defaultValue: string
): string => {
  switch (cellWidth.type) {
    case "FIXED_134":
      return "134px";
    case "MIN":
      return "100%";
    case "FIXED":
      return `${cellWidth.value}px`;
    default:
      return exhaustiveGuardWithDefaultValue(cellWidth, defaultValue);
  }
};

export const getProductXScrollListStyle = (
  node: PartialNode<IProductXScrollListNode>,
  language = "ko"
) => {
  const style: CSSProperties = {};

  style.overflow = "hidden";

  if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";

        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (node.cornerRadius) {
    style["borderRadius"] = getBorderRadiusStyle(node.cornerRadius);
  }

  if (node.effects) {
    style["boxShadow"] = getShadowStyle([...node.effects]);
  }

  if (node.fills) {
    style["background"] = getPaintsStyle(node.fills);
  }

  // 내부 요소가 아닌, 노드 자체 정렬
  if (
    node.alignHorizontal &&
    (!node.position || node.position.type === "RELATIVE")
  ) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  if (node.aspectRatio) {
    style["aspectRatio"] = getAspectRatioStyle(node.aspectRatio);
  }

  Object.assign(style, {
    "--cb-product-item-fixed-width": node.cellWidth
      ? getProductItemWidth(node.cellWidth, "134px")
      : "134px",
    "--cb-product-image-border-radius": node.imageCornerRadius
      ? getBorderRadiusStyle(node.imageCornerRadius)
      : "8px",
    "--cb-product-image-aspect-ratio": node.imageAspectRatio
      ? getAspectRatioStyle(node.imageAspectRatio)
      : "1 / 1",
    "--cb-product-image-object-fit": node.imageObjectFit
      ? getObjectFitStyle(node.imageObjectFit)
      : "cover",
    "--cb-product-text-container-padding": node.textContainerPadding
      ? getPaddingStyle(node.textContainerPadding)
      : "4px 0px 0px",
    "--cb-product-text-container-align-items":
      node.textContainerCounterAxisAlignItems
        ? getCounterAxisAlignItemsStyle(node.textContainerCounterAxisAlignItems)
        : "left",
    "--cb-product-text-container-text-align":
      node.textContainerCounterAxisAlignItems === "MAX"
        ? "right"
        : node.textContainerCounterAxisAlignItems === "CENTER"
        ? "center"
        : "left",
    "--cb-product-text-container-background-color":
      node.textContainerBackgroundColor
        ? getRgbString(node.textContainerBackgroundColor)
        : "rgba(255, 255, 255, 0)",
    "--cb-product-title-font-family": node.productTitleFontName
      ? getFontFamily({
          fontName: node.productTitleFontName,
          language,
        })
      : "inherit",
    "--cb-product-title-font-weight": node.productTitleFontName
      ? node.productTitleFontName.style
      : "normal",
    "--cb-product-title-font-size": node.productTitleFontSize
      ? getUnitStyle(node.productTitleFontSize)
      : "14px",
    "--cb-product-title-color": node.productTitleColor
      ? getRgbString(node.productTitleColor)
      : "inherit",
    "--cb-product-original-price-font-family": node.originalPriceFontName
      ? getFontFamily({
          fontName: node.originalPriceFontName,
          language,
        })
      : "inherit",
    "--cb-product-original-price-font-weight": node.originalPriceFontName
      ? node.originalPriceFontName.style
      : "normal",
    "--cb-product-original-price-font-size": node.originalPriceFontSize
      ? getUnitStyle(node.originalPriceFontSize)
      : "12px",
    "--cb-product-original-price-color": node.originalPriceColor
      ? getRgbString(node.originalPriceColor)
      : "rgba(152, 158, 150, 1)",
    "--cb-product-original-price-margin-top": node.originalPriceMarginTop
      ? getUnitStyle(node.originalPriceMarginTop)
      : "4px",
    "--cb-product-price-container-margin-top": node.priceContainerMarginTop
      ? getUnitStyle(node.priceContainerMarginTop)
      : "4px",
    "--cb-product-price-container-gap":
      typeof node.priceContainerGap === "number"
        ? `${node.priceContainerGap}px`
        : "8px",
    "--cb-product-discount-ratio-font-family": node.discountRatioFontName
      ? getFontFamily({
          fontName: node.discountRatioFontName,
          language,
        })
      : "inherit",
    "--cb-product-discount-ratio-font-weight": node.discountRatioFontName
      ? node.discountRatioFontName.style
      : "600",
    "--cb-product-discount-ratio-font-size": node.discountRatioFontSize
      ? getUnitStyle(node.discountRatioFontSize)
      : "14px",
    "--cb-product-discount-ratio-color": node.discountRatioColor
      ? getRgbString(node.discountRatioColor)
      : "rgb(255, 88, 25)",
    "--cb-product-final-price-font-family": node.finalPriceFontName
      ? getFontFamily({
          fontName: node.finalPriceFontName,
          language,
        })
      : "inherit",
    "--cb-product-final-price-font-weight": node.finalPriceFontName
      ? node.finalPriceFontName.style
      : "600",
    "--cb-product-final-price-font-size": node.finalPriceFontSize
      ? getUnitStyle(node.finalPriceFontSize)
      : "14px",
    "--cb-product-final-price-color": node.finalPriceColor
      ? getRgbString(node.finalPriceColor)
      : "rgba(27, 28, 27, 1)",
  });

  // TODO: Reaction 필요할 시 추가

  // Padding 필요할 시 추가
  // if (shouldApplyPaddingStyle(node.padding)) {
  //   style["padding"] = getPaddingStyle(node.padding);
  // }

  return style;
};

export const getAutomatedProductListStyle = (
  node: PartialNode<IAutomatedProductListNode>,
  language = "ko"
) => {
  const style: CSSProperties = {};

  style.overflow = "hidden";

  if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";

        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (node.cornerRadius) {
    style["borderRadius"] = getBorderRadiusStyle(node.cornerRadius);
  }

  if (node.effects) {
    style["boxShadow"] = getShadowStyle([...node.effects]);
  }

  if (node.fills) {
    style["background"] = getPaintsStyle(node.fills);
  }

  // 내부 요소가 아닌, 노드 자체 정렬
  if (
    node.alignHorizontal &&
    (!node.position || node.position.type === "RELATIVE")
  ) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  if (node.aspectRatio) {
    style["aspectRatio"] = getAspectRatioStyle(node.aspectRatio);
  }

  Object.assign(style, {
    "--cb-product-card-background-color": node.productCardBackgroundColor
      ? getRgbString(node.productCardBackgroundColor)
      : "rgba(255, 255, 255, 1)",
    "--cb-product-card-border-radius": node.productCardBorderRadius
      ? getBorderRadiusStyle(node.productCardBorderRadius)
      : "0px",
    "--cb-product-card-padding": node.productCardPadding
      ? getPaddingStyle(node.productCardPadding)
      : "0px",
    "--cb-product-card-direction":
      node.productCardDirection === "ROW" ? "row" : "column",
    //2025/10/23 - 현대홈쇼핑 팝업 가로 스크롤 문제로 하나만 보여주기 위해 셀 너비를 강제로 100%로 변경
    //   ? getProductItemWidth(node.cellWidth, "288px")
    //   : "288px",
    "--cb-product-item-fixed-width": "100%",
    "--cb-product-image-border-radius": node.imageCornerRadius
      ? getBorderRadiusStyle(node.imageCornerRadius)
      : "0px",
    "--cb-product-image-aspect-ratio": node.imageAspectRatio
      ? getAspectRatioStyle(node.imageAspectRatio)
      : "1 / 1",
    "--cb-product-image-object-fit": node.imageObjectFit
      ? getObjectFitStyle(node.imageObjectFit)
      : "cover",
    "--cb-product-text-container-padding": node.textContainerPadding
      ? getPaddingStyle(node.textContainerPadding)
      : "4px 0px 0px",
    "--cb-product-text-container-align-items":
      node.textContainerCounterAxisAlignItems
        ? getCounterAxisAlignItemsStyle(node.textContainerCounterAxisAlignItems)
        : "left",
    "--cb-product-text-container-text-align":
      node.textContainerCounterAxisAlignItems === "MAX"
        ? "right"
        : node.textContainerCounterAxisAlignItems === "CENTER"
        ? "center"
        : "left",
    "--cb-product-text-container-background-color":
      node.textContainerBackgroundColor
        ? getRgbString(node.textContainerBackgroundColor)
        : "rgba(255, 255, 255, 0)",
    "--cb-product-title-font-family": node.productTitleFontName
      ? getFontFamily({
          fontName: node.productTitleFontName,
          language,
        })
      : "inherit",
    "--cb-product-title-font-weight": node.productTitleFontName
      ? node.productTitleFontName.style
      : "normal",
    "--cb-product-title-font-size": node.productTitleFontSize
      ? getUnitStyle(node.productTitleFontSize)
      : "14px",
    "--cb-product-title-color": node.productTitleColor
      ? getRgbString(node.productTitleColor)
      : "inherit",
    "--cb-product-original-price-font-family": node.originalPriceFontName
      ? getFontFamily({
          fontName: node.originalPriceFontName,
          language,
        })
      : "inherit",
    "--cb-product-original-price-font-weight": node.originalPriceFontName
      ? node.originalPriceFontName.style
      : "normal",
    "--cb-product-original-price-font-size": node.originalPriceFontSize
      ? getUnitStyle(node.originalPriceFontSize)
      : "12px",
    "--cb-product-original-price-color": node.originalPriceColor
      ? getRgbString(node.originalPriceColor)
      : "rgba(152, 158, 150, 1)",
    "--cb-product-original-price-margin-top": node.originalPriceMarginTop
      ? getUnitStyle(node.originalPriceMarginTop)
      : "4px",
    "--cb-product-price-container-margin-top": node.priceContainerMarginTop
      ? getUnitStyle(node.priceContainerMarginTop)
      : "4px",
    "--cb-product-price-container-gap":
      typeof node.priceContainerGap === "number"
        ? `${node.priceContainerGap}px`
        : "4px",
    "--cb-product-discount-ratio-font-family": node.discountRatioFontName
      ? getFontFamily({
          fontName: node.discountRatioFontName,
          language,
        })
      : "inherit",
    "--cb-product-discount-ratio-font-weight": node.discountRatioFontName
      ? node.discountRatioFontName.style
      : "600",
    "--cb-product-discount-ratio-font-size": node.discountRatioFontSize
      ? getUnitStyle(node.discountRatioFontSize)
      : "14px",
    "--cb-product-discount-ratio-color": node.discountRatioColor
      ? getRgbString(node.discountRatioColor)
      : "rgb(255, 88, 25)",
    "--cb-product-final-price-font-family": node.finalPriceFontName
      ? getFontFamily({
          fontName: node.finalPriceFontName,
          language,
        })
      : "inherit",
    "--cb-product-final-price-font-weight": node.finalPriceFontName
      ? node.finalPriceFontName.style
      : "600",
    "--cb-product-final-price-font-size": node.finalPriceFontSize
      ? getUnitStyle(node.finalPriceFontSize)
      : "18px",
    "--cb-product-final-price-color": node.finalPriceColor
      ? getRgbString(node.finalPriceColor)
      : "rgba(27, 28, 27, 1)",
  });

  // TODO: Reaction 필요할 시 추가

  // Padding 필요할 시 추가
  // if (shouldApplyPaddingStyle(node.padding)) {
  //   style["padding"] = getPaddingStyle(node.padding);
  // }

  return style;
};

export const getProductGridListStyle = (
  node: PartialNode<IProductGridListNode>,
  language = "ko"
) => {
  const style: CSSProperties = {};

  style.overflow = "hidden";

  if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";

        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (node.cornerRadius) {
    style["borderRadius"] = getBorderRadiusStyle(node.cornerRadius);
  }

  if (node.effects) {
    style["boxShadow"] = getShadowStyle([...node.effects]);
  }

  if (node.fills) {
    style["background"] = getPaintsStyle(node.fills);
  }

  // 내부 요소가 아닌, 노드 자체 정렬
  if (
    node.alignHorizontal &&
    (!node.position || node.position.type === "RELATIVE")
  ) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  if (node.aspectRatio) {
    style["aspectRatio"] = getAspectRatioStyle(node.aspectRatio);
  }

  // TODO: Reaction 필요할 시 추가

  // Padding 필요할 시 추가
  // if (shouldApplyPaddingStyle(node.padding)) {
  //   style["padding"] = getPaddingStyle(node.padding);
  // }

  Object.assign(style, {
    "--cb-product-grid-list-column": node.col,
    "--cb-product-grid-list-row": node.row,

    "--cb-product-grid-list-gap":
      typeof node.gap === "number" ? `${node.gap}px` : "16px",
    "--cb-product-item-fixed-width": node.cellWidth
      ? getProductItemWidth(node.cellWidth, "134px")
      : "134px",
    "--cb-product-image-border-radius": node.imageCornerRadius
      ? getBorderRadiusStyle(node.imageCornerRadius)
      : "8px",
    "--cb-product-image-aspect-ratio": node.imageAspectRatio
      ? getAspectRatioStyle(node.imageAspectRatio)
      : "1 / 1",
    "--cb-product-image-object-fit": node.imageObjectFit
      ? getObjectFitStyle(node.imageObjectFit)
      : "cover",
    "--cb-product-text-container-padding": node.textContainerPadding
      ? getPaddingStyle(node.textContainerPadding)
      : "4px 0px 0px",
    "--cb-product-text-container-align-items":
      node.textContainerCounterAxisAlignItems
        ? getCounterAxisAlignItemsStyle(node.textContainerCounterAxisAlignItems)
        : "left",
    "--cb-product-text-container-text-align":
      node.textContainerCounterAxisAlignItems === "MAX"
        ? "right"
        : node.textContainerCounterAxisAlignItems === "CENTER"
        ? "center"
        : "left",
    "--cb-product-text-container-background-color":
      node.textContainerBackgroundColor
        ? getRgbString(node.textContainerBackgroundColor)
        : "rgba(255, 255, 255, 0)",
    "--cb-product-title-font-family": node.productTitleFontName
      ? getFontFamily({
          fontName: node.productTitleFontName,
          language,
        })
      : "inherit",
    "--cb-product-title-font-weight": node.productTitleFontName
      ? node.productTitleFontName.style
      : "normal",
    "--cb-product-title-font-size": node.productTitleFontSize
      ? getUnitStyle(node.productTitleFontSize)
      : "14px",
    "--cb-product-title-color": node.productTitleColor
      ? getRgbString(node.productTitleColor)
      : "inherit",
    "--cb-product-original-price-font-family": node.originalPriceFontName
      ? getFontFamily({
          fontName: node.originalPriceFontName,
          language,
        })
      : "inherit",
    "--cb-product-original-price-font-weight": node.originalPriceFontName
      ? node.originalPriceFontName.style
      : "normal",
    "--cb-product-original-price-font-size": node.originalPriceFontSize
      ? getUnitStyle(node.originalPriceFontSize)
      : "12px",
    "--cb-product-original-price-color": node.originalPriceColor
      ? getRgbString(node.originalPriceColor)
      : "rgba(152, 158, 150, 1)",
    "--cb-product-original-price-margin-top": node.originalPriceMarginTop
      ? getUnitStyle(node.originalPriceMarginTop)
      : "4px",
    "--cb-product-price-container-margin-top": node.priceContainerMarginTop
      ? getUnitStyle(node.priceContainerMarginTop)
      : "4px",
    "--cb-product-price-container-gap":
      typeof node.priceContainerGap === "number"
        ? `${node.priceContainerGap}px`
        : "8px",
    "--cb-product-discount-ratio-font-family": node.discountRatioFontName
      ? getFontFamily({
          fontName: node.discountRatioFontName,
          language,
        })
      : "inherit",
    "--cb-product-discount-ratio-font-weight": node.discountRatioFontName
      ? node.discountRatioFontName.style
      : "600",
    "--cb-product-discount-ratio-font-size": node.discountRatioFontSize
      ? getUnitStyle(node.discountRatioFontSize)
      : "14px",
    "--cb-product-discount-ratio-color": node.discountRatioColor
      ? getRgbString(node.discountRatioColor)
      : "rgb(255, 88, 25)",
    "--cb-product-final-price-font-family": node.finalPriceFontName
      ? getFontFamily({
          fontName: node.finalPriceFontName,
          language,
        })
      : "inherit",
    "--cb-product-final-price-font-weight": node.finalPriceFontName
      ? node.finalPriceFontName.style
      : "600",
    "--cb-product-final-price-font-size": node.finalPriceFontSize
      ? getUnitStyle(node.finalPriceFontSize)
      : "14px",
    "--cb-product-final-price-color": node.finalPriceColor
      ? getRgbString(node.finalPriceColor)
      : "rgba(27, 28, 27, 1)",
  });

  return style;
};

export const getSwiperContainerStyle = (
  node: PartialNode<ISwiperContainerNode>
) => {
  const style: CSSProperties = {};

  style.overflow = "hidden";

  if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";

        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (node.cornerRadius) {
    style["borderRadius"] = getBorderRadiusStyle(node.cornerRadius);
  }

  if (node.effects) {
    style["boxShadow"] = getShadowStyle([...node.effects]);
  }

  if (node.fills) {
    style["background"] = getPaintsStyle(node.fills);
  }

  // 내부 요소가 아닌, 노드 자체 정렬
  if (
    node.alignHorizontal &&
    (!node.position || node.position.type === "RELATIVE")
  ) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  if (node.aspectRatio) {
    if (
      node.aspectRatio.type === "MANUAL" &&
      node.aspectRatio.widthRatio &&
      node.aspectRatio.heightRatio
    ) {
      Object.assign(style, {
        "--cb-swiper-container-aspect-ratio": `${
          (node.aspectRatio.heightRatio * 100) / node.aspectRatio.widthRatio
        }%`,
      });
    }
  }

  if (
    node.position?.type === "RELATIVE" &&
    !node.aspectRatio &&
    node.width &&
    node.width.unit === "px"
  ) {
    style["left"] = `calc(min(0px, (100% - ${node.width.value}px) / 2))`;
  }

  return style;
};

export const getSwiperPaginationStyle = (
  node: PartialNode<ISwiperPaginationNode>
) => {
  const style: CSSProperties = {};
  style["overflow"] = "hidden";

  style["display"] = "flex";

  if (node.padding) {
    style["padding"] = getPaddingStyle(node.padding);
  }

  // 진행방향 내부 요소 정렬
  if (node.primaryAxisAlignItems) {
    style["justifyContent"] = getPrimaryAxisAlignItemsStyle(
      node.primaryAxisAlignItems
    );
  }

  // 진행방향의 수직방향 내부 요소 정렬
  if (node.counterAxisAlignItems) {
    style["alignItems"] = getCounterAxisAlignItemsStyle(
      node.counterAxisAlignItems
    );
  }

  if (node.style?.type === "BULLET") {
    style["gap"] = `${node.style.gap}px`;
  } else if (node.style?.type === "FRACTION") {
    // TODO: 설정 속성 선정 시 변수로 받도록 설정 필요
    style["textAlign"] = "center";
  }

  style["width"] = node.width ? getUnitStyle(node.width) : "auto";

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";

        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (node.fills) {
    style["background"] = getPaintsStyle(node.fills);
  }

  if (node.cornerRadius) {
    style["borderRadius"] = getBorderRadiusStyle(node.cornerRadius);
  }

  /** TODO: 이펙트 종류 추가시 로직 변경해야 함.
   *  현재는 effects에 DropShadow 요소 밖에 없어서 로직이 간단함. */
  if (node.effects) {
    style["boxShadow"] = getShadowStyle([...node.effects]);
  }

  if (
    node.reactions?.some(
      (reaction) =>
        reaction.trigger?.type &&
        cursorPointerTriggers.includes(reaction.trigger.type) &&
        // Action이 URL일 때는 action.url이 존재하는 경우에만 cursor pointer 적용
        (reaction.action?.type !== "URL" ||
          (typeof reaction.action?.url === "string" &&
            reaction.action?.url !== ""))
    )
  ) {
    style["cursor"] = "pointer";
  }
  return style;
};

export const getSwiperPaginationBulletStyle = (
  nodeStyle: PartialNode<ISwiperPaginationBullet>
) => {
  const style: CSSProperties = {};

  if (nodeStyle.color) {
    style["backgroundColor"] = getRgbString(
      nodeStyle.color,
      colord(nodeStyle.color).alpha()
    );
  }

  if (nodeStyle.size) {
    style["width"] = `${nodeStyle.size}px`;
    style["height"] = `${nodeStyle.size}px`;
    style["borderRadius"] = `${nodeStyle.size / 2}px`;
  }

  style["cursor"] = "pointer";
  return style;
};

export const getSwiperPaginationBulletActiveStyle = (
  nodeStyle: PartialNode<ISwiperPaginationBullet>
) => {
  const style: CSSProperties = {};

  if (nodeStyle.activeColor) {
    style["backgroundColor"] = getRgbString(
      nodeStyle.activeColor,
      colord(nodeStyle.activeColor).alpha()
    );

    style["transitionTimingFunction"] = "cubic-bezier(0.4, 0, 0.2, 1)";
    style["transitionDuration"] = "150ms";
  }

  if (nodeStyle.size) {
    style["width"] = `${nodeStyle.size}px`;
    style["height"] = `${nodeStyle.size}px`;
    style["borderRadius"] = `${nodeStyle.size / 2}px`;
  }
  return style;
};

export const getSwiperPaginationFractionStyle = (
  node: PartialNode<ISwiperPaginationFraction>,
  language = "ko"
) => {
  const style: CSSProperties = {};

  if (node.color) {
    style["color"] = getRgbString(node.color, colord(node.color).alpha());
  }

  if (node.fontName) {
    style["fontFamily"] = getFontFamily({
      fontName: node.fontName,
      language,
      fixed: true,
    });
    style["fontWeight"] = node.fontName.style;
    style["fontFeatureSettings"] = "'tnum'";
    style["fontVariantNumeric"] = "tabular-nums";
  }

  if (node.fontSize) {
    style["fontSize"] = getUnitStyle(node.fontSize);
  }

  if (node.letterSpacing) {
    style["letterSpacing"] = getUnitStyle(node.letterSpacing);
  }

  if (node.fontSize) {
    style["fontSize"] = getUnitStyle(node.fontSize);
  }

  return style;
};

export const getSwiperButtonGroupStyle = (
  node: PartialNode<ISwiperButtonGroupNode>
) => {
  const style: CSSProperties = {};

  style["display"] = "flex";
  style["justifyContent"] = "space-between";

  if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";

        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (
    node.alignHorizontal &&
    (!node.position || node.position.type === "RELATIVE")
  ) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  if (
    node.reactions?.some(
      (reaction) =>
        reaction.trigger?.type &&
        cursorPointerTriggers.includes(reaction.trigger.type) &&
        // Action이 URL일 때는 action.url이 존재하는 경우에만 cursor pointer 적용
        (reaction.action?.type !== "URL" ||
          (typeof reaction.action?.url === "string" &&
            reaction.action?.url !== ""))
    )
  ) {
    style["cursor"] = "pointer";
  }

  style["pointerEvents"] = "none";
  return style;
};

export const getSwiperButtonStyle = (
  nodeStyle: PartialNode<ISwiperButtonGroupChevron>
) => {
  const style: CSSProperties = {};

  if (nodeStyle.color) {
    style["color"] = getRgbString(
      nodeStyle.color,
      colord(nodeStyle.color).alpha()
    );
  }

  if (nodeStyle.padding) {
    style["padding"] = getPaddingStyle(nodeStyle.padding);
  }

  if (nodeStyle.cornerRadius) {
    style["borderRadius"] = getBorderRadiusStyle(nodeStyle.cornerRadius);
  }

  if (nodeStyle.backgrounds) {
    style["background"] = getPaintsStyle(nodeStyle.backgrounds);
  }

  if (nodeStyle.effects) {
    style["boxShadow"] = getShadowStyle([...nodeStyle.effects]);
  }

  style["cursor"] = "pointer";
  style["pointerEvents"] = "all";
  style["height"] = "100%";
  return style;
};

export const getInstagramGalleryItemStyle = (
  node: PartialNode<IInstagramGalleryNode>
) => {
  const style: CSSProperties = {};

  style.position = "relative";
  style.display = "block";
  style.aspectRatio = "1 / 1"; // 기본값

  if (node.feedRatio) {
    style.aspectRatio = node.feedRatio;
  }

  return style;
};

export const getInstagramGalleryImageStyle = (
  node: PartialNode<IInstagramGalleryNode>
) => {
  const style: CSSProperties = {};

  style.width = "100%";
  style.objectFit = "cover";
  style.aspectRatio = "1 / 1"; // 기본값

  if (node.feedRatio) {
    style.aspectRatio = node.feedRatio;
  }

  return style;
};

export const instagramGalleryCaptionWrapperStyle: CSSProperties = {
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  color: "white",
  transition: "opacity 0.2s",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  padding: "10%",
};

export const instagramGalleryIconStyle: CSSProperties = {
  width: "15%",
  height: "15%",
  maxWidth: "28px",
  maxHeight: "28px",
  position: "absolute",
  color: "white",
  right: "8px",
  top: "8px",
  opacity: 0.8,
};

export const instagramGalleryCaptionStyle: CSSProperties = {
  WebkitLineClamp: 4,
  WebkitBoxOrient: "vertical",
  display: "-webkit-box",
  wordBreak: "keep-all",
  overflow: "hidden",
};

export const instagramGalleryLinkAreaStyle: CSSProperties = {
  position: "absolute",
  width: "100%",
  height: "100%",
  top: 0,
  left: 0,
};

/**
 * box shadow 스타일을 계산하는 함수.
 *
 * 이펙트는 우선 그림자밖에 없으므로 그림자만 처리함.
 * 추후 다른 종류의 이펙트가 추가될 때 로직 수정해야 함.
 */
export const getShadowStyle = (effects: Effect[]) => {
  return effects
    .filter(
      (effect): effect is IDropShadowEffect => effect.type === "DROP_SHADOW"
    )
    .map(
      (item) =>
        `${item.offset.x ? `${item.offset.x}px` : 0} ${
          item.offset.y ? `${item.offset.y}px` : 0
        } ${item.radius ? `${item.radius}px` : 0} ${
          item.spread ? `${item.spread}px` : 0
        } ${colord(item.color).toRgbString()}`
    )
    .join(", ");
};

export const getTextShadowStyle = (effects: Effect[]) => {
  return effects
    .filter(
      (effect): effect is IDropShadowEffect => effect.type === "DROP_SHADOW"
    )
    .map(
      (item) =>
        `${item.offset.x ? `${item.offset.x}px` : 0} ${
          item.offset.y ? `${item.offset.y}px` : 0
        } ${item.radius ? `${item.radius}px` : 0} ${colord(
          item.color
        ).toRgbString()}`
    )
    .join(", ");
};

export const styleToString = (style: CSSProperties) => {
  return Object.keys(style).reduce((acc, key) => {
    let styleName = key
      .split(/(?=[A-Z])/)
      .join("-")
      .toLowerCase();
    if (styleName.startsWith("webkit")) {
      styleName = `-${styleName}`;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return `${acc}${styleName}:${(style as any)[key]};`;
  }, "");
};

export const getFormStyle = (
  node: PartialNode<IFormNode>,
  language: string = "ko"
) => {
  const style: CSSProperties = {};

  style["display"] = "flex";
  style["flexDirection"] = "column";

  if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }
  if (node.height) {
    style["height"] = "auto";
  }
  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";

        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (node.padding) {
    style["padding"] = getPaddingStyle(node.padding);
  }

  if (node.cornerRadius) {
    style["borderRadius"] = getBorderRadiusStyle(node.cornerRadius);
  }

  if (node.effects) {
    style["boxShadow"] = getShadowStyle([...node.effects]);
  }

  if (node.fills) {
    style["background"] = getPaintsStyle(node.fills);
  }

  // 내부 요소가 아닌, 노드 자체 정렬
  if (
    node.alignHorizontal &&
    (!node.position || node.position.type === "RELATIVE")
  ) {
    style["margin"] = getMarginByAlignHorizontal(node.alignHorizontal);
  }

  if (node.gap) {
    style["gap"] = `${node.gap}px`;
  }

  if (node.fontName) {
    style["fontFamily"] = getFontFamily({
      fontName: node.fontName,
      language,
    });
    style["fontWeight"] = node.fontName.style;
  }

  if (node.fontSize) {
    style["fontSize"] = getUnitStyle(node.fontSize);
  }

  if (node.fontColor) {
    style["color"] = getRgbString(
      node.fontColor,
      colord(node.fontColor).alpha()
    );
  }

  Object.assign(
    style,
    {
      "--cb-form-base-color": getColorVar(node.baseColor || "#D0D0D0"),
      "--cb-form-primary-color": getColorVar(node.primaryColor || "#3617CE"),
      "--cb-form-font-size": getUnitStyle(node.fontSize),
      "--cb-form-font-color": getColorVar(node.fontColor || "#474747"),
      "--cb-text-xs": getUnitStyle(node.fontSize, 0.75),
      "--cb-line-height-xs": getUnitStyle(node.fontSize, 1.125),
      "--cb-text-sm": getUnitStyle(node.fontSize, 0.8125),
      "--cb-line-height-sm": getUnitStyle(node.fontSize, 1.125),
      "--cb-text-base": getUnitStyle(node.fontSize, 0.875),
      "--cb-line-height-base": getUnitStyle(node.fontSize, 1.375),
      "--cb-text-md": getUnitStyle(node.fontSize, 0.9375),
      "--cb-line-height-md": getUnitStyle(node.fontSize, 1.40625),
      "--cb-text-lg": getUnitStyle(node.fontSize, 1),
      "--cb-line-height-lg": getUnitStyle(node.fontSize, 1.5),
      "--cb-text-xl": getUnitStyle(node.fontSize, 1.25),
      "--cb-line-height-xl": getUnitStyle(node.fontSize, 1.875),
      color: "rgba(var(--cb-form-font-color), 1)",
      fontSize: "var(--cb-form-font-size)",
      fontFamily: "var(--cb-form-font-family)",
    },
    node.fontName?.family
      ? { "--cb-form-font-family": node.fontName?.family }
      : {}
  );

  return style;
};

export const getFormDividerStyle = ({
  formNode,
  node,
}: {
  formNode?: PartialNode<IFormNode> | null;
  node: PartialNode<IFormDividerNode>;
}) => {
  const style: CSSProperties = {};

  style.border = 0;
  style.borderBottom = "1px solid";
  style.width = "100%";
  style.height = "0px";

  if (node.margin) {
    style["margin"] = getMarginStyle(node.margin);
  }

  if (formNode?.baseColor) {
    style["borderColor"] = getRgbString(
      formNode.baseColor,
      colord(formNode.baseColor).alpha()
    );
  }

  return style;
};

// 이미지 로딩중 영역을 미리 잡아놓기 위해 wrapper를 사용
export const getFormImageVideoStyle = (
  node: PartialNode<IFormImageVideoNode>
) => {
  const style: CSSProperties = {};

  style.overflow = "hidden";

  /**
   * iOS 버전 17이하에서 border-radius 속성이 있고, overflow-hidden일 때, video 태그에 border-radius가 적용되지 않는 이슈를 해결하기 위한 속성.
   * @see https://gist.github.com/domske/b66047671c780a238b51c51ffde8d3a0
   */
  if (
    node.cornerRadius?.bl?.value ||
    node.cornerRadius?.br?.value ||
    node.cornerRadius?.tl?.value ||
    node.cornerRadius?.tr?.value
  ) {
    style.isolation = "isolate";
  }

  if (node.width) {
    style["width"] = getUnitStyle(node.width);
  }

  if (node.height) {
    style["height"] = getUnitStyle(node.height);
  } else if (node.position?.type === "ABSOLUTE") {
    style["height"] = "auto";
  }

  if (node.position) {
    switch (node.position.type) {
      case "RELATIVE":
        style["position"] = "relative";
        break;
      case "ABSOLUTE":
        style["position"] = "absolute";

        Object.assign(style, getConstraintStyle(node.position));
        break;
    }
  }

  if (node.margin) {
    style["margin"] = getMarginStyle(node.margin);
  }

  if (node.aspectRatio) {
    style["aspectRatio"] = getAspectRatioStyle(node.aspectRatio);
  }

  if (node.cornerRadius) {
    style["borderRadius"] = getBorderRadiusStyle(node.cornerRadius);
  }

  /**
   * TODO: testing-library/react에서 렌더링 시 background 속성 내 gradient
   * 관련 함수가 모두 무시되어 테스팅이 불가능
   * @see https://github.com/jsdom/cssstyle/issues/148
   */
  if (node.fills) {
    style["background"] = getPaintsStyle(node.fills);
  }

  /** TODO: 이펙트 종류 추가시 로직 변경해야 함.
   *  현재는 effects에 DropShadow 요소 밖에 없어서 로직이 간단함. */
  if (node.effects) {
    style["boxShadow"] = getShadowStyle([...node.effects]);
  }

  return style;
};

export const getDefaultFontName = (language?: string, fixed = false) => {
  if (fixed) {
    return DEFAULT_FIXED_FONT_NAME;
  }

  if (language === "en") {
    return DEFAULT_EN_FONT_NAME;
  }
  if (language === "ja") {
    return DEFAULT_JA_FONT_NAME;
  }

  return DEFAULT_FONT_NAME;
};

export const getOverflowStyle = (overflow: OverflowDirection) => {
  return overflow === "HORIZONTAL"
    ? "auto hidden"
    : overflow === "VERTICAL"
    ? "hidden auto"
    : "hidden";
};
